<script lang="ts" setup>
const props = defineProps<{
  data: {
    id: string
    translations: DirectusTranslation<{
      title: string
    }>
    categories: {
      id: number
      block_faq_collection_category_id: {
        id: number
        items: {
          id: number
          block_faq_entry_id: {
            id: number
            translations: DirectusTranslation<{
              title: string
              description: string
            }>
          }
        }[]
        translations: DirectusTranslation<{
          title: string
        }>
      }
    }[]
  }
}>()

const { t } = useI18n()
const { dt } = useDirectusTranslation()
const { hyphen } = useHyphen()

const faqItems = computed(() => props.data.categories.map((category) => {
  return {
    id: category.block_faq_collection_category_id.id,
    title: dt(category.block_faq_collection_category_id.translations)?.title ?? '',
    items: category.block_faq_collection_category_id.items.map((item) => {
      return {
        id: item.block_faq_entry_id.id,
        question: dt(item.block_faq_entry_id.translations)?.title ?? '',
        answer: dt(item.block_faq_entry_id.translations)?.description ?? '',
      }
    }),
  }
}))

const filteredFaqItems = computed(() => {
  return faqItems.value.filter(faqItem => faqItem.title !== '')
})
</script>

<template>
  <BlockBase class="relative py-16rem">
    <BlockBase
      as="div"
      is-light class="absolute inset-x-0 bottom-0 top-70rem bg-neutral-white lg:top-50rem"
    />
    <Container class="relative">
      <h1 class="mb-3rem text-center text-2.5rem font-title lg:mb-2rem lg:text-left">
        {{ hyphen(dt(props.data.translations)?.title) }}
      </h1>
      <RadixTabsRoot :default-value="String(filteredFaqItems[0]?.id)" orientation="vertical">
        <div class="grid grid-cols-1 gap-4rem lg:grid-cols-[1fr_2fr]">
          <RadixTabsList class="flex flex-wrap justify-center gap-1.5rem lg:flex-col lg:justify-start">
            <RadixTabsTrigger
              v-for="category in filteredFaqItems" :key="category.id" :value="String(category.id)"
              class="inline-block cursor-pointer select-none bg-starline-secondary px-1.6rem py-.8rem text-left text-1.3rem tracking-[.4rem] uppercase ring-px ring-neutral-darkGray transition duration-300ms data-[state=active]:relative data-[state=active]:shadow-starline data-[state=active]:ring-3px data-[state=active]:ring-starline-primary lg:mr-auto hover:ring-starline-primary"
            >
              {{ category.title }}
            </RadixTabsTrigger>
          </RadixTabsList>
          <div class="">
            <RadixTabsContent v-for="category in filteredFaqItems" :key="category.id" :value="String(category.id)" class="relative">
              <div class="absolute border-px border-neutral-darkGray bg-starline-secondary -inset-px" />
              <div class="bg-gradient absolute -inset-px" />
              <RadixAccordionRoot
                v-if="category.items.length"
                type="single"
                :collapsible="true"
                class="relative bg-starline-secondary px-2rem py-.2rem divide-y divide-neutral-darkGray"
              >
                <RadixAccordionItem v-for="item in category.items" :key="item.id" :value="String(item.id)">
                  <RadixAccordionHeader class="-mx-2rem">
                    <RadixAccordionTrigger class="group w-full flex items-center justify-between gap-2rem px-2rem py-1.5rem">
                      <div class="wrap-balance text-left text-1.6rem">
                        {{ item.question }}
                      </div>
                      <div aria-hidden class="group:[data-open] i-radix-icons-plus flex-shrink-0 transform text-2.4rem duration-300 group-data-[state=open]:rotate-45" />
                    </RadixAccordionTrigger>
                  </RadixAccordionHeader>
                  <RadixAccordionContent class="AccordionContent overflow-hidden border-t-px border-neutral-darkGray">
                    <div class="my-1.75em max-w-full text-1.6rem text-neutral-white/85 prose prose-invert block-light:prose-neutral" v-html="item.answer" />
                  </RadixAccordionContent>
                </RadixAccordionItem>
              </RadixAccordionRoot>
              <p v-else class="relative py-2rem text-center text-1.6rem text-neutral-gray">
                {{ t('faq.category_empty') }}
              </p>
            </RadixTabsContent>
          </div>
        </div>
      </RadixTabsRoot>
    </Container>
  </BlockBase>

  <template v-for="category in filteredFaqItems" :key="category.id">
    <template v-for="item in category.items" :key="item.id">
      <SchemaOrgQuestion>
        <template #name>
          {{ item.question }}
        </template>
        <template #acceptedAnswer>
          <p>{{ item.answer }}</p>
        </template>
      </SchemaOrgQuestion>
    </template>
  </template>
</template>

<style scoped>
@keyframes slideDown {
  from {
    height: 0;
  }
  to {
    height: var(--radix-accordion-content-height);
  }
}

@keyframes slideUp {
  from {
    height: var(--radix-accordion-content-height);
  }
  to {
    height: 0;
  }
}
.AccordionContent {
  overflow: hidden;
}
.AccordionContent[data-state='open'] {
  animation: slideDown 300ms cubic-bezier(0.87, 0, 0.13, 1);
}
.AccordionContent[data-state='closed'] {
  animation: slideUp 300ms cubic-bezier(0.87, 0, 0.13, 1);
}

.bg-gradient {
  background-image: linear-gradient(to bottom, transparent 0%, #000b1a 50%);
}
</style>
